/**
 * Phase 19.0 — Producer Department promotion rails.
 *
 * Surfaces four independent producer-side rails ABOVE the marketplace grid:
 *   - Featured Beats
 *   - Beat of the Week
 *   - Trending Beats
 *   - Top Producers
 *
 * Intentionally separate from the artist-side Discover rails so producer
 * promotions get their own lane and rankings.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import BeatCard from "@/components/BeatCard";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import { useCurrentUser } from "@/lib/useCurrentUser";

const Row = ({ children }) => (
  <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
    {children}
  </div>
);

const SectionHeader = ({ icon, title, subtitle, action }) => (
  <div className="flex items-end justify-between gap-3 mb-3">
    <div>
      <h3 className="text-lg sm:text-xl font-black text-white flex items-center gap-2">
        <span>{icon}</span>
        <span>{title}</span>
      </h3>
      {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const ProducerLanes = () => {
  const { currentUser } = useCurrentUser();
  const currentUserId = currentUser?.user_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mostGiftedBeats, setMostGiftedBeats] = useState([]);
  const [mostGiftedProducers, setMostGiftedProducers] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/beats/discover/lanes")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    // Phase 19.4 — fetch leaderboards in parallel (non-blocking)
    api.get("/beats/leaderboard/most-gifted?window_days=30").then((r) => {
      if (alive) setMostGiftedBeats(r.data?.results || []);
    }).catch(() => {});
    api.get("/producers/leaderboard/most-gifted?window_days=30").then((r) => {
      if (alive) setMostGiftedProducers(r.data?.results || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (loading || !data) return null;

  const { featured_beats: featured, beat_of_the_week: botw, trending_beats: trending, top_producers: producers } = data;
  const hasAnything = (featured?.length || 0) + (trending?.length || 0) + (producers?.length || 0) + (botw ? 1 : 0) + (mostGiftedBeats?.length || 0) + (mostGiftedProducers?.length || 0) > 0;
  if (!hasAnything) return null;

  return (
    <div className="space-y-8 mb-6" data-testid="producer-lanes">
      {/* 🎖️ Beat of the Week — hero card */}
      {botw && (
        <section data-testid="lane-beat-of-the-week">
          <SectionHeader
            icon="🎖️"
            title="Beat of the Week"
            subtitle="Producer Department · the standout beat this week"
          />
          <Link
            to={`/beats/${botw.beat_id}`}
            className="block p-3 rounded-xl bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border border-amber-500/40 hover:border-amber-300/80 transition-colors"
            data-testid={`botw-card-${botw.beat_id}`}
          >
            <div className="flex items-center gap-4">
              <img
                src={optimizeImage(botw.cover_url || "/rgm-logo.png", 'card')}
                alt={botw.title}
                className="w-20 h-20 rounded-lg object-cover bg-black border border-amber-400/40"
                onError={(e) => { e.target.src = "/rgm-logo.png"; }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-amber-200 text-[10px] uppercase tracking-widest font-bold">🎖️ Beat of the Week</p>
                <p className="text-white text-xl font-black truncate">{botw.title}</p>
                <p className="text-xs text-gray-300 truncate">
                  by {botw.producer?.artist_name || botw.producer?.name || "Producer"}
                  {botw.bpm ? ` · ${botw.bpm} BPM` : ""}
                  {botw.genre ? ` · ${botw.genre}` : ""}
                </p>
                <p className="text-[11px] text-gray-400 mt-1">
                  ❤️ {botw.like_count || 0} · ▶ {(botw.plays || 0).toLocaleString()} plays
                </p>
              </div>
              <span className="text-amber-300 text-sm font-bold">Listen →</span>
            </div>
          </Link>
        </section>
      )}

      {/* 🏆 Featured Beats */}
      {featured && featured.length > 0 && (
        <section data-testid="lane-featured-beats">
          <SectionHeader
            icon="🏆"
            title="Featured Beats"
            subtitle="Producer Department · admin-curated picks"
          />
          <Row>
            {featured.slice(0, 12).map((b) => (
              <div key={b.beat_id} className="w-56 flex-shrink-0 snap-start">
                <BeatCard beat={b} currentUserId={currentUserId} />
              </div>
            ))}
          </Row>
        </section>
      )}

      {/* 🔥 Trending Beats */}
      {trending && trending.length > 0 && (
        <section data-testid="lane-trending-beats">
          <SectionHeader
            icon="🔥"
            title="Trending Beats"
            subtitle="Producer Department · most played in the last 7 days"
          />
          <Row>
            {trending.map((b) => (
              <div key={b.beat_id} className="w-56 flex-shrink-0 snap-start">
                <BeatCard beat={b} currentUserId={currentUserId} />
              </div>
            ))}
          </Row>
        </section>
      )}

      {/* 🎁 Most Gifted Beats (Phase 19.4) */}
      {mostGiftedBeats && mostGiftedBeats.length > 0 && (
        <section data-testid="lane-most-gifted-beats">
          <SectionHeader
            icon="🎁"
            title="Most Gifted Beats"
            subtitle="Producer Department · listeners voting with coins (30d)"
          />
          <Row>
            {mostGiftedBeats.slice(0, 12).map((b) => (
              <div key={b.beat_id} className="w-56 flex-shrink-0 snap-start relative">
                <BeatCard beat={b} currentUserId={currentUserId} />
                <span
                  className="absolute top-2 left-2 px-2 py-0.5 text-[10px] rounded-full bg-amber-500/90 text-black font-black"
                  data-testid={`gift-badge-${b.beat_id}`}
                >
                  🎁 {b.total_coins?.toLocaleString?.() || b.total_coins} coins
                </span>
              </div>
            ))}
          </Row>
        </section>
      )}

      {/* 👑 Top Producers */}
      {producers && producers.length > 0 && (
        <section data-testid="lane-top-producers">
          <SectionHeader
            icon="👑"
            title="Top Producers"
            subtitle="Producer Department · ranked by plays, likes & downloads"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {producers.slice(0, 10).map((p, i) => (
              <Link
                key={p.user_id}
                to={`/artist/${p.user_id}`}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-gray-900/40 border border-gray-800 hover:border-purple-500/40 transition-colors"
                data-testid={`top-producer-${p.user_id}`}
              >
                <div className="relative">
                  <img
                    src={getProfilePhotoUrl(p)}
                    alt={p.artist_name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-purple-500/40"
                  />
                  <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs font-black flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>
                <p className="text-white text-sm font-bold text-center truncate w-full">{p.artist_name || p.name}</p>
                <p className="text-[10px] text-gray-400 text-center">
                  {(p.stats?.plays || 0).toLocaleString()} plays
                  {p.stats?.downloads ? ` · ${p.stats.downloads} dl` : ""}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
      {/* 💎 Most Gifted Producers (Phase 19.4) */}
      {mostGiftedProducers && mostGiftedProducers.length > 0 && (
        <section data-testid="lane-most-gifted-producers">
          <SectionHeader
            icon="💎"
            title="Most Gifted Producers"
            subtitle="Producer Department · highest-earning producers (30d)"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {mostGiftedProducers.slice(0, 10).map((p, i) => (
              <Link
                key={p.user_id}
                to={`/artist/${p.user_id}`}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/30 hover:border-amber-300/70 transition-colors"
                data-testid={`most-gifted-producer-${p.user_id}`}
              >
                <div className="relative">
                  <img
                    src={getProfilePhotoUrl(p)}
                    alt={p.artist_name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-amber-400/60"
                  />
                  <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300 text-black text-xs font-black flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>
                <p className="text-white text-sm font-bold text-center truncate w-full">{p.artist_name || p.name}</p>
                <p className="text-[10px] text-amber-200 text-center font-bold">
                  🎁 {(p.total_coins || 0).toLocaleString()} coins
                </p>
                <p className="text-[10px] text-gray-400 text-center">
                  {p.gift_count} gifts · {p.unique_sender_count} fans
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProducerLanes;
